import { useQuery } from '@tanstack/react-query';
import {
    QuestionnaireFormData,
    viewModelToBusinessDocTypeForm,
} from '../features/Questionnaire/QuestionnaireCRUD.utils';
import { backendServices } from '../services';
import { BusinessDocType, FieldDictionaryUsedBy } from '../types';

/**
 * Loads active SIM questionnaires configured for a buyer company.
 * Mirrors the data loaded by `QuestionnaireCRUDService.fetchEntity` per subtype.
 * The endpoints require an authenticated user, so callers that run before
 * login completes must gate the fetch via `enabled`.
 */
export function useBuyerQuestionnaires({
    buyerCompanyId,
    enabled = true,
}: {
    buyerCompanyId: number | null | undefined;
    enabled?: boolean;
}) {
    return useQuery<QuestionnaireFormData[]>(
        ['BuyerQuestionnaires', buyerCompanyId],
        async () => {
            const businessDocTypeApi = new backendServices.Apis.BusinessDocTypeApi();
            const documentApi = new backendServices.Apis.DocumentApi();

            const { data: businessDocTypes } = await businessDocTypeApi.getBusinessDocType({
                $filter: `BusinessDocTypeId eq ${BusinessDocType.SIMDocument} and CompanyId eq ${buyerCompanyId}`,
            });

            const activeQuestionnaires = businessDocTypes.filter(
                (record) => record.Active && record.BusinessDocSubTypeId != null
            );

            return Promise.all(
                activeQuestionnaires.map(async (record) => {
                    const businessDocSubTypeId = record.BusinessDocSubTypeId!;

                    const { data: fieldDictionaryLayoutViewModel } = await documentApi.getDocumentLayout({
                        businessDocType: BusinessDocType.SIMDocument,
                        culture: 'en-US',
                        client: FieldDictionaryUsedBy.BuyerPortal,
                        documentClass: businessDocSubTypeId.toString(),
                        buyerCompanyId: buyerCompanyId!,
                    });

                    return viewModelToBusinessDocTypeForm(record, fieldDictionaryLayoutViewModel);
                })
            );
        },
        {
            enabled: enabled && buyerCompanyId != null,
            staleTime: Infinity,
            cacheTime: Infinity,
        }
    );
}
